import { formatDate } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  ActionSheetController,
  AlertController,
  LoadingController,
  ModalController,
  ToastController,
} from '@ionic/angular';
import { Case } from 'src/app/lost-found/case.model';
import { CommentsModalComponent } from '../comments-modal/comments-modal.component';
import { CaseService } from 'src/app/lost-found/case.service';
import { Swiper } from 'swiper/types';
import { environment } from 'src/environments/environment';
import { Share } from '@capacitor/share';
import { AuthService } from 'src/app/auth/auth.service';
import { Clipboard } from '@capacitor/clipboard';
import { PhotoService } from '../services/photo.service';
import { ReportService } from '../services/report.service';
import { ChatService } from 'src/app/chats/chats.service';
@Component({
  selector: 'app-case-card',
  templateUrl: './case-card.component.html',
  styleUrls: ['./case-card.component.scss'],
})
export class CaseCardComponent implements OnInit {
  @Input('casee') casee: Case;
  @Input('cases') cases: Case[];
  @ViewChild('swiper') swiperRef: ElementRef | undefined;
  swiper!: Swiper;
  hideDelete = true;
  caseType = '';
  userId = 0;
  toggleValue = false;
  isAdmin = false;
  async presentActionSheet(casee) {
    const isUserAdded = casee.userid === this.userId || this.isAdmin;
    let complete = '';
    let icon = '';
    if (casee.isClosed) {
      complete = 'Reopen Case';
      icon = 'refresh-circle-outline';
    } else {
      complete = 'Mark as Solved';
      icon = 'checkmark-done-outline';
    }
    const buttons = [
      {
        text: 'Copy Link',
        icon: 'copy-outline',
        handler: () => {
          this.copyLink(casee.id);
        },
      },
      {
        text: 'Share Case',
        icon: 'share-social-outline',
        handler: () => {
          this.shareCase(casee.id);
        },
      },
      // Conditionally add the Delete button
      ...(isUserAdded
        ? [
          {
            text: 'Edit Case',
            icon: 'pencil-outline',
            handler: () => {
              this.editCase(casee.id);
            },
          },
          {
            text: complete,
            icon: icon,
            handler: () => {
              this.toggleClosed(casee.id);
            },
          },
          {
            text: 'Delete',
            icon: 'trash-outline',
            role: 'destructive',
            handler: () => {
              this.deleteCase(casee.id);
            },
            cssClass: 'danger-button',
          },
        ]
        : []), // Empty if condition is false
      ...(!isUserAdded
        ? [
          {
            text: 'Report Post',
            icon: 'nuclear-outline',
            role: 'destructive',
            handler: () => {
              this.reportCase(casee.id);
            },
            cssClass: 'danger-button',
          },
        ]
        : []),
    ];

    const actionSheet = await this.actionSheetCtrl.create({
      buttons: buttons,
    });

    await actionSheet.present();
  }

  async copyLink(caseId: number) {
    const url = environment.appUrl + this.router.url + '/' + caseId;
    try {
      await Clipboard.write({
        string: url,
      });

      const toast = await this.toastController.create({
        message: 'URL copied to clipboard!',
        color: 'primary',
        duration: 2000,
        position: 'bottom',
      });
      toast.present();
    } catch (error) {
      console.error('Failed to copy URL:', error);
      const toast = await this.toastController.create({
        message: 'Failed to copy URL.',
        duration: 2000,
        position: 'bottom',
      });
      toast.present();
    }
  }

  onToggleChanged(event: any) {
    this.toggleValue = event.detail.checked;
    // Additional logic
  }
  constructor(
    private router: Router,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private toastController: ToastController,
    private caseService: CaseService,
    private authService: AuthService,
    private actionSheetCtrl: ActionSheetController,
    private photoService: PhotoService,
    private reportService: ReportService,
    private chatService: ChatService
  ) { }

  async ngOnInit() {
    this.isAdmin = await this.authService.isAdmin();
    this.userId = await this.authService.getUserId();
    this.hideDelete = this.router.url.toLowerCase().includes('/cases');
    switch (this.casee.type) {
      case 'adoption': {
        this.caseType = 'FOR ADOPTION';
        break;
      }
      case 'lost': {
        this.caseType = 'LOST PET';
        break;
      }
      case 'found': {
        this.caseType = 'FOUND PET';
        break;
      }
      case 'foster': {
        this.caseType = 'FOSTER NEEDED';
        break;
      }
      case 'cashdonation': {
        this.caseType = 'CASH DONATION NEEDED';
        break;
      }
      case 'supplies': {
        this.caseType = 'Pet Supplies Donation Needed';
        break;
      }
      case 'report': {
        this.caseType = 'CRUELTY REPORT';
        break;
      }

      default: {
        this.caseType = 'OTHER';
        break;
      }
    }
  }

  formatDate(myDate: Date) {
    const format = 'MMMM dd yyyy';
    const locale = 'en-US';
    const formattedDate = formatDate(myDate, format, locale);
    return formattedDate;
  }

  ViewComments(caseId: number, caseUserId: number) {
    if (this.userId <= 0) {
      this.ShowLoginAlert();
      return;
    }
    this.modalCtrl
      .create({
        component: CommentsModalComponent,
        componentProps: {
          caseId,
          userId: this.userId,
          postUserId: caseUserId,
          source: 'case',
        },
      })
      .then((modalEl) => {
        modalEl.onDidDismiss().then((modalData) => {
          if (!modalData.data) {
            return;
          }
          const index = this.cases.findIndex((c) => c.id === caseId);
          this.cases[index].commentsCount = modalData.data['commentsCount'];
        });
        modalEl.present();
      });
  }

  viewDetails(id: number) {
    this.router.navigateByUrl('cases/' + id);
  }

  editCase(id: number) {
    this.router.navigateByUrl('account/mycases/edit-case/' + id);
  }

  deleteCase(caseId: number) {
    this.alertCtrl
      .create({
        header: 'Delete Case?',
        message: 'Are you sure you want to delete this case?',
        buttons: [
          {
            text: 'Cancel',
            handler: () => {
              this.alertCtrl.dismiss();
            },
          },
          {
            text: 'Delete',
            cssClass: 'confirm-delete',
            handler: () => {
              this.performDelete(caseId);
              this.alertCtrl.dismiss();
            },
          },
        ],
      })
      .then((alertEl) => alertEl.present());
  }

  performDelete(caseId: number) {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        message: 'Please wait...',
      })
      .then((loadingEl) => {
        loadingEl.present();
        const caseObj = this.cases.find((c) => c.id === caseId)
        caseObj.images.forEach(img => {
          this.photoService.deleteImageFromFirebase(img.url);
        });
        this.caseService.deleteCase(caseId).subscribe({
          next: async (res) => {
            if (res) {
              const index = this.cases.findIndex((p) => p.id === caseId);
              this.cases.splice(index, 1);
              loadingEl.dismiss();
              const toast = await this.toastController.create({
                color: 'primary',
                duration: 2000,
                message: 'Case deleted successfully.',
              });
              await toast.present();
            } else {
              loadingEl.dismiss();
              const toast = await this.toastController.create({
                color: 'danger',
                duration: 2000,
                message: 'Could not perform action. Please try again later.',
              });
              await toast.present();
            }
          },
          error: async () => {
            loadingEl.dismiss();
            const toast = await this.toastController.create({
              color: 'danger',
              duration: 2000,
              message: 'Could not perform action. Please try again later.',
            });
            await toast.present();
          },
        });
      });
  }

  toggleClosed(caseId: number) {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        message: 'Please wait...',
      })
      .then((loadingEl) => {
        loadingEl.present();
        this.caseService.toggleClosed(caseId).subscribe({
          next: async (res) => {
            if (res) {
              const index = this.cases.findIndex((p) => p.id === caseId);
              this.cases[index].isClosed = !this.cases[index].isClosed;
              loadingEl.dismiss();
              const toast = await this.toastController.create({
                color: 'primary',
                duration: 2000,
                message: 'Case updated successfully.',
              });
              await toast.present();
            } else {
              loadingEl.dismiss();
              const toast = await this.toastController.create({
                color: 'danger',
                duration: 2000,
                message: 'Could not perform action. Please try again later.',
              });
              await toast.present();
            }
          },
          error: async () => {
            loadingEl.dismiss();
            const toast = await this.toastController.create({
              color: 'danger',
              duration: 2000,
              message: 'Could not perform action. Please try again later.',
            });
            await toast.present();
          },
        });
      });
  }

  async shareCase(id: number) {
    const url = environment.appUrl + this.router.url + '/' + id;
    await Share.share({
      title: 'Help Needed',
      text: `Can you help with this case? ${url}`, // Combine text and URL
    });
  }

  reportCase(caseId: number) {
    this.reportService.report('case', caseId.toString(), this.userId);
  }


  ShowLoginAlert() {
    this.alertCtrl
      .create({
        header: 'Sign In Reqiured',
        message: 'You need to sign in first to view or add comments',
        buttons: [
          {
            text: 'Dismiss',
            handler: () => {
              this.alertCtrl.dismiss();
            },
          },
          {
            text: 'Sign in',
            handler: () => {
              this.router.navigateByUrl('/signin?redirectURL=cases');
            },
          },
        ],
      })
      .then((alertEl) => alertEl.present());
  }

  openUserOptions(userId: number, username: string) {
    this.chatService.openUserOptions(userId.toString(), username);
  }

}
